// Slack-style team chat. Channels + DMs. Mobile-first responsive drawer.
// Works identically on desktop, phone browser, and inside the TechApp.
//
// Design notes:
// - Sidebar collapses to a slide-in drawer under 720px; hamburger toggles it.
// - Channels grouped: CHANNELS (public), DIRECT MESSAGES (1:1).
// - User avatars are colored initial circles hashed from the user id — no
//   uploads to store.
// - Message grouping: same sender within 5 minutes = tight stack, only the
//   first message shows name + timestamp.
// - Polls channels + messages every 4s; drops payload if the pane isn't visible.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Input, Textarea, Spinner } from '../ui.jsx';

const AVATAR_PALETTE = ['#8b5cf6','#22d3ee','#10b981','#f59e0b','#ef4444','#ec4899','#3b82f6','#a855f7','#84cc16','#f97316','#14b8a6','#6366f1'];
function avatarColor(id) {
  const s = String(id || '');
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length-1][0]).toUpperCase();
}
function Avatar({ userId, name, size = 34 }) {
  return <div style={{
    width: size, height: size, borderRadius: '50%',
    background: avatarColor(userId), color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: Math.round(size * 0.42), flexShrink: 0,
    fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    userSelect: 'none',
  }}>{initials(name)}</div>;
}

// Simple viewport hook — used to know if we're on mobile so we can flip
// the layout into a drawer. Runs cheaply on resize (throttled implicit via
// react re-render).
function useIsNarrow(bp = 720) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < bp);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < bp);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return narrow;
}

export function TeamChat({ user, toast, embedded }) {
  const [channels, setChannels] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [dmPickerOpen, setDmPickerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [teammates, setTeammates] = useState([]);
  const [mentionQ, setMentionQ] = useState(null); // { term, start } for autocomplete
  const [mentionIdx, setMentionIdx] = useState(0);
  const narrow = useIsNarrow(720);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  // Load teammates once for @mention autocomplete. Includes an "all" token.
  useEffect(() => {
    api('/api/team-chat/users').then(r => setTeammates(r.users || [])).catch(()=>{});
  }, []);

  const loadChannels = useCallback(() => {
    return api('/api/team-chat/channels').then(r => {
      setChannels(r.channels || []);
      if (!activeId && (r.channels || []).length > 0) setActiveId(r.channels[0].id);
    }).catch(() => setChannels([]));
  }, [activeId]);

  const loadMessages = useCallback(() => {
    if (!activeId) return;
    api(`/api/team-chat/channels/${activeId}/messages?limit=200`)
      .then(r => setMessages(r.messages || []))
      .catch(() => {});
  }, [activeId]);

  useEffect(() => { loadChannels(); }, []);
  useEffect(() => { loadMessages(); }, [activeId]);
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => { loadMessages(); loadChannels(); }, 4000);
    return () => clearInterval(t);
  }, [activeId, loadMessages, loadChannels]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    setSending(true);
    try {
      await api(`/api/team-chat/channels/${activeId}/messages`, { method: 'POST', body: { body: body.trim() }});
      setBody('');
      loadMessages();
      textareaRef.current?.focus();
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setSending(false); }
  };
  const createChannel = async () => {
    if (!newChannelName.trim()) return;
    try {
      const r = await api('/api/team-chat/channels', { method: 'POST', body: { name: newChannelName.trim() }});
      setNewChannelName(''); setCreatingChannel(false);
      await loadChannels();
      setActiveId(r.id);
      if (narrow) setDrawerOpen(false);
    } catch (e) { toast?.(e.message, 'error'); }
  };
  const pickChannel = (id) => {
    setActiveId(id);
    if (narrow) setDrawerOpen(false);
  };
  const activeChannel = channels?.find(c => c.id === activeId);

  // Autocomplete candidates for the current @-term. First name / "all" match.
  const mentionCandidates = React.useMemo(() => {
    if (!mentionQ) return [];
    const q = mentionQ.term.toLowerCase();
    const all = [{ id: '__all__', displayName: 'all', email: 'Notify everyone in this channel', role: 'special' },
      ...teammates.map(t => ({ ...t }))];
    return all.filter(t => t.displayName.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)).slice(0, 6);
  }, [mentionQ, teammates]);

  const onBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const caret = e.target.selectionStart ?? val.length;
    // Look backwards from caret for an @ that isn't preceded by a word char.
    // Terminates at any whitespace between @ and caret (that means the token
    // has closed).
    const upTo = val.slice(0, caret);
    const m = /(^|\s)@([a-zA-Z0-9._\-]{0,20})$/.exec(upTo);
    if (m) { setMentionQ({ term: m[2], start: caret - m[2].length - 1 }); setMentionIdx(0); }
    else setMentionQ(null);
  };
  const applyMention = (candidate) => {
    if (!mentionQ) return;
    const token = candidate.id === '__all__' ? 'all' : (candidate.displayName.split(/\s+/)[0].toLowerCase());
    const before = body.slice(0, mentionQ.start);
    const after = body.slice(mentionQ.start + 1 + mentionQ.term.length);
    const next = `${before}@${token} ${after}`;
    setBody(next);
    setMentionQ(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };
  const onBodyKeyDown = (e) => {
    if (mentionQ && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionCandidates.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); applyMention(mentionCandidates[mentionIdx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMentionQ(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !narrow) { e.preventDefault(); send(); }
  };

  if (!channels) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner sz={32}/></div>;

  const containerHeight = embedded ? '100%' : 'calc(100vh - 64px)';
  const showSidebar = !narrow || drawerOpen;
  const showMessages = !narrow || !drawerOpen;

  const totalUnread = channels.reduce((n, c) => n + (c.unread || 0), 0);

  return <div style={{ display: 'flex', height: containerHeight, overflow: 'hidden', position: 'relative', background: G.bg }}>
    {/* Sidebar / drawer */}
    {showSidebar && (
      <>
        {narrow && <div onClick={() => setDrawerOpen(false)}
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 40 }}/>}
        <div style={{
          width: narrow ? 'min(300px, 82vw)' : 280,
          borderRight: `1px solid ${G.border}`,
          background: G.paper,
          display: 'flex', flexDirection: 'column',
          position: narrow ? 'absolute' : 'relative',
          left: 0, top: 0, bottom: 0, zIndex: 50,
          boxShadow: narrow ? '4px 0 20px rgba(0,0,0,.3)' : 'none',
        }}>
          <div style={{ padding: '16px 18px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>💬 Team Chat</h1>
              <div style={{ fontSize: 11, color: G.textMuted, marginTop: 2 }}>{user?.account?.name || 'Your team'}</div>
            </div>
            {narrow && <button onClick={() => setDrawerOpen(false)} style={closeBtn}>×</button>}
          </div>

          <div style={{ padding: '0 8px', flex: 1, overflow: 'auto' }}>
            {(() => {
              const publicChans = channels.filter(c => !c.isDm);
              const dms = channels.filter(c => c.isDm);
              return <>
                <SectionHeader label="Channels"/>
                {publicChans.map(c => <ChannelRow key={c.id} c={c} isActive={c.id === activeId} onClick={() => pickChannel(c.id)}/>)}
                {publicChans.length === 0 && <EmptyRow label="No channels yet"/>}
                <SectionHeader label="Direct Messages" style={{ marginTop: 16 }}/>
                {dms.map(c => <ChannelRow key={c.id} c={c} isActive={c.id === activeId} onClick={() => pickChannel(c.id)} isDm/>)}
                {dms.length === 0 && <EmptyRow label="No DMs — tap + DM to start"/>}
              </>;
            })()}
          </div>

          <div style={{ padding: '10px 12px', borderTop: `1px solid ${G.border}`, display: 'flex', gap: 8 }}>
            {creatingChannel ? (
              <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                <Input value={newChannelName} onChange={e => setNewChannelName(e.target.value)}
                  placeholder="channel name" style={{ fontSize: 13 }} autoFocus
                  onKeyDown={e => e.key === 'Enter' && createChannel()}/>
                <Btn sz="xs" v="primary" onClick={createChannel}>+</Btn>
                <Btn sz="xs" onClick={() => { setCreatingChannel(false); setNewChannelName(''); }}>×</Btn>
              </div>
            ) : (
              <>
                <button onClick={() => setCreatingChannel(true)} style={{ ...linkBtn, color: G.cyan, textAlign: 'left' }}>+ Channel</button>
                <button onClick={() => setDmPickerOpen(true)} style={{ ...linkBtn, color: G.purple, textAlign: 'right' }}>+ DM</button>
              </>
            )}
          </div>
        </div>
      </>
    )}

    {/* Message pane */}
    {showMessages && <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: 12, background: G.card, flexShrink: 0 }}>
        {narrow && (
          <button onClick={() => setDrawerOpen(true)} style={hamburger}>
            <span>☰</span>
            {totalUnread > 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: G.red, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{totalUnread}</span>}
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {activeChannel ? (activeChannel.isDm ? <>💬 {activeChannel.name}</> : <># {activeChannel.name}</>) : 'Team Chat'}
          </div>
          {activeChannel?.description && <div style={{ fontSize: 11, color: G.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeChannel.description}</div>}
        </div>
      </div>

      {!activeChannel ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: G.textMuted, padding: 40, textAlign: 'center' }}>
          {narrow ? <button onClick={() => setDrawerOpen(true)} style={pickBtn}>Pick a channel</button>
                  : 'Pick a channel on the left to start chatting.'}
        </div>
      ) : <>
        <div ref={listRef} style={{ flex: 1, overflow: 'auto', padding: '14px 16px 8px' }}>
          {messages.length === 0 ? (
            <div style={{ color: G.textMuted, textAlign: 'center', padding: 40, fontSize: 14 }}>
              {activeChannel.isDm ? `Say hi to ${activeChannel.name}.` : 'Nothing here yet. Kick things off with a message.'}
            </div>
          ) : messages.map((m, i) => {
            const prev = messages[i-1];
            const sameGroup = prev && prev.senderUserId === m.senderUserId && (new Date(m.createdAt) - new Date(prev.createdAt) < 5*60*1000);
            return <MessageRow key={m.id} m={m} sameGroup={sameGroup}/>;
          })}
        </div>

        <div style={{ padding: '10px 12px 14px', borderTop: `1px solid ${G.border}`, background: G.card, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              {mentionQ && mentionCandidates.length > 0 && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 6,
                  background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflow: 'hidden',
                  boxShadow: '0 6px 18px rgba(0,0,0,.4)', zIndex: 5 }}>
                  <div style={{ fontSize: 10, color: G.textMuted, padding: '6px 12px', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>People</div>
                  {mentionCandidates.map((c, i) => (
                    <div key={c.id} onMouseDown={e => { e.preventDefault(); applyMention(c); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        background: i === mentionIdx ? G.purpleDim : 'transparent', cursor: 'pointer' }}>
                      {c.id === '__all__' ? <div style={{ width: 28, height: 28, borderRadius: 14, background: G.red, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>@</div>
                                          : <Avatar userId={c.id} name={c.displayName} size={28}/>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{c.displayName}</div>
                        <div style={{ fontSize: 11, color: G.textMuted }}>{c.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Textarea ref={textareaRef} value={body} onChange={onBodyChange} onKeyDown={onBodyKeyDown}
                placeholder={activeChannel.isDm ? `Message ${activeChannel.name}` : `Message #${activeChannel.name} — use @name to notify`}
                style={{ resize: 'none', minHeight: narrow ? 46 : 44, maxHeight: 200, fontSize: 15, width: '100%' }}/>
            </div>
            <Btn v="primary" sz={narrow ? 'md' : 'sm'} onClick={send} disabled={sending || !body.trim()}
              style={narrow ? { minWidth: 74, padding: '14px 18px' } : undefined}>
              {sending ? '…' : narrow ? 'Send' : 'Send'}
            </Btn>
          </div>
          {!narrow && <div style={{ fontSize: 11, color: G.textMuted, marginTop: 6 }}>Enter to send · Shift+Enter for newline</div>}
        </div>
      </>}
    </div>}

    {dmPickerOpen && <DmPickerModal
      onClose={() => setDmPickerOpen(false)}
      onOpened={cid => { setDmPickerOpen(false); loadChannels(); setActiveId(cid); if (narrow) setDrawerOpen(false); }}
      toast={toast}/>}
  </div>;
}

function SectionHeader({ label, style }) {
  return <div style={{ fontSize: 10, color: G.textMuted, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, padding: '8px 12px 4px', ...style }}>{label}</div>;
}
function EmptyRow({ label }) {
  return <div style={{ padding: '6px 12px', color: G.textMuted, fontSize: 12, fontStyle: 'italic' }}>{label}</div>;
}
function ChannelRow({ c, isActive, isDm, onClick }) {
  return <button onClick={onClick}
    style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
      width: '100%', textAlign: 'left', border: 'none',
      background: isActive ? G.purpleDim : 'transparent',
      padding: '10px 12px', borderRadius: 8, marginBottom: 2, cursor: 'pointer',
      color: isActive ? G.purple : G.text,
      minHeight: 44 }}>
    {isDm ? <Avatar userId={c.id} name={c.name} size={30}/> : <span style={{ fontSize: 18, width: 30, textAlign: 'center' }}>#</span>}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: c.unread > 0 ? 800 : 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
        {c.unread > 0 && <span style={{ background: G.red, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 10, flexShrink: 0 }}>{c.unread}</span>}
      </div>
      {c.lastMessage && <div style={{ fontSize: 11, color: G.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.lastMessage.body}</div>}
    </div>
  </button>;
}

function MessageRow({ m, sameGroup }) {
  return <div style={{ display: 'flex', gap: 10, marginTop: sameGroup ? 2 : 12 }}>
    <div style={{ width: 38, flexShrink: 0 }}>
      {!sameGroup && <Avatar userId={m.senderUserId} name={m.senderName} size={38}/>}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      {!sameGroup && <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
        <span style={{ fontWeight: 800, color: m.isMe ? G.purple : G.text, fontSize: 14 }}>{m.senderName}{m.isMe && <span style={{ color: G.textMuted, fontWeight: 500, marginLeft: 4 }}>(you)</span>}</span>
        <span style={{ fontSize: 10, color: G.textMuted }}>{new Date(m.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
      </div>}
      <div style={{ fontSize: 14.5, lineHeight: 1.5, color: G.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
    </div>
  </div>;
}

const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '8px 0', flex: 1 };
const closeBtn = { background: 'none', border: 'none', color: G.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 4 };
const pickBtn = { background: G.purple, color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', fontSize: 15 };
const hamburger = { background: 'none', border: `1px solid ${G.border}`, color: G.text, fontSize: 20, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, position: 'relative' };

// Picker for starting a DM. Fetches teammates, click to open. Idempotent
// on the server — clicking the same person twice re-opens the same DM.
function DmPickerModal({ onClose, onOpened, toast }) {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState('');
  const narrow = useIsNarrow(720);
  useEffect(() => { api('/api/team-chat/users').then(r => setUsers(r.users || [])).catch(()=>setUsers([])); }, []);
  const startDm = async (uid) => {
    try {
      const r = await api('/api/team-chat/dms', { method: 'POST', body: { targetUserId: uid }});
      onOpened(r.id);
    } catch (e) { toast?.(e.message, 'error'); }
  };
  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 9999, display: 'flex', alignItems: narrow ? 'stretch' : 'flex-start', justifyContent: 'center', padding: narrow ? 0 : '50px 16px' };
  const card = { background: G.card, borderRadius: narrow ? 0 : 14, border: narrow ? 'none' : `1px solid ${G.border}`, width: '100%', maxWidth: narrow ? '100%' : 440, padding: '22px 18px 24px', display: 'flex', flexDirection: 'column', maxHeight: narrow ? '100vh' : '78vh' };
  const filtered = (users || []).filter(u => !q.trim() || u.displayName.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));
  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>Start a Direct Message</div>
        <button onClick={onClose} style={closeBtn}>×</button>
      </div>
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name or email" autoFocus/>
      <div style={{ marginTop: 12, overflow: 'auto', flex: 1 }}>
        {users === null ? <Spinner sz={20}/> : filtered.length === 0 ? (
          <div style={{ color: G.textMuted, fontSize: 13, padding: 14, textAlign: 'center' }}>No teammates match.</div>
        ) : filtered.map(u => (
          <button key={u.id} onClick={() => startDm(u.id)}
            style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', border: 'none',
              background: 'transparent', padding: '10px 12px', borderRadius: 8, marginBottom: 2, cursor: 'pointer', color: G.text, minHeight: 52 }}
            onMouseEnter={e => e.currentTarget.style.background = G.input}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Avatar userId={u.id} name={u.displayName} size={40}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{u.displayName}</div>
              <div style={{ fontSize: 11, color: G.textMuted }}>{u.email} · {u.role}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>;
}
